import React from "react"

import { Checkbox, shouldNavigateRow, Tooltip } from "@coval/ui"

import {
  KEYBOARD_ACTIVE_ROW_CLASSES,
  KEYBOARD_ACTIVE_ROW_STYLE,
} from "@/lib/keyboardNavigationStyles"
import type { RunRowLike } from "@/types/RunRow"
import { cn } from "@/utils/cn"

import type { RunResultsExplanationTable } from "../../hooks/useRunResultsColumnDefs"
import type { RunResultsMetricRenderAdaptersById } from "../../types"
import { renderTableCell } from "../RenderTableCell"

interface GroupedRawRowProps {
  customDimensionGroupBySimId?: Record<string, string>
  customDimensionGroupColorById?: Record<string, string>
  customDimensionGroupNameById?: Record<string, string>
  explanationColumns: Array<{ key: string }>
  explanationTable: RunResultsExplanationTable
  indentClass: string
  isKeyboardActive: boolean
  metricColumns: Array<{ key: string }>
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  onPointerEnter: (simulationOutputId: string | null) => void
  onPointerLeave: (simulationOutputId: string | null) => void
  onRowClick: (simulationOutputId: string) => void
  onToggleSelection: (simulationOutputId: string) => void
  row: RunRowLike
  rowIndex: number
  selected: boolean
  selectionEnabled: boolean
}

function hexToRgba(hex: string, alpha: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return hex
  }
  const red = parseInt(hex.slice(1, 3), 16)
  const green = parseInt(hex.slice(3, 5), 16)
  const blue = parseInt(hex.slice(5, 7), 16)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

export function GroupedRawRow({
  customDimensionGroupBySimId,
  customDimensionGroupColorById,
  customDimensionGroupNameById,
  explanationColumns,
  explanationTable,
  indentClass,
  isKeyboardActive,
  metricColumns,
  metricRenderAdaptersById,
  onPointerEnter,
  onPointerLeave,
  onRowClick,
  onToggleSelection,
  row,
  rowIndex,
  selected,
  selectionEnabled,
}: GroupedRawRowProps) {
  const simulationOutputId = row.simulation_output_id
  const rowGroupId =
    customDimensionGroupBySimId && simulationOutputId
      ? customDimensionGroupBySimId[simulationOutputId]
      : undefined
  const rowGroupColor = rowGroupId
    ? customDimensionGroupColorById?.[rowGroupId]
    : undefined
  const rowStyle: React.CSSProperties = isKeyboardActive
    ? { ...KEYBOARD_ACTIVE_ROW_STYLE }
    : {}
  if (rowGroupColor && !isKeyboardActive) {
    rowStyle.backgroundColor = hexToRgba(rowGroupColor, 0.1)
    rowStyle.boxShadow = `inset 3px 0 0 0 ${rowGroupColor}`
  }
  const groupName = rowGroupId
    ? (customDimensionGroupNameById?.[rowGroupId] ?? rowGroupId)
    : "Unassigned"

  return (
    <tr
      className={cn(
        "border-b border-coval-border bg-surface-0 hover:bg-surface-1 cursor-pointer",
        simulationOutputId && isKeyboardActive && KEYBOARD_ACTIVE_ROW_CLASSES,
      )}
      style={rowStyle}
      data-grouped-simulation-output-id={simulationOutputId}
      onClick={(event) => {
        // The row is the open target, so every cell in it opens the simulation
        // — except a click that landed on a control the cell owns (a metric's
        // rerun button, an error's copy button), which drives that control.
        if (simulationOutputId && shouldNavigateRow(event.target)) {
          onRowClick(simulationOutputId)
        }
      }}
      onMouseEnter={() => onPointerEnter(simulationOutputId ?? null)}
      onMouseLeave={() => onPointerLeave(simulationOutputId ?? null)}
    >
      {selectionEnabled && (
        <td
          className={`text-sm py-1.5 px-3 align-middle w-10 ${indentClass}`}
          onClick={(event) => event.stopPropagation()}
        >
          {simulationOutputId ? (
            <Checkbox
              aria-label="Select simulation"
              checked={selected}
              onCheckedChange={() => onToggleSelection(simulationOutputId)}
            />
          ) : null}
        </td>
      )}
      <td
        className={`text-sm py-1.5 px-3 align-middle text-coval-text-muted ${indentClass}`}
      >
        <span className="text-xs text-coval-text-muted mr-2">
          {rowIndex + 1}.
        </span>
        <span className="text-xs text-coval-text-muted">
          {simulationOutputId ? simulationOutputId.slice(0, 4) : "-"}
        </span>
        {customDimensionGroupBySimId && simulationOutputId && (
          <span
            className={cn(
              "ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
              !rowGroupId &&
                "bg-surface-0 text-coval-text-muted border border-coval-border",
            )}
            style={
              rowGroupColor
                ? {
                    backgroundColor: hexToRgba(rowGroupColor, 0.2),
                    color: rowGroupColor,
                    border: `1px solid ${hexToRgba(rowGroupColor, 0.5)}`,
                  }
                : undefined
            }
          >
            {groupName}
          </span>
        )}
      </td>
      {metricColumns.map((column) => (
        <td
          key={column.key}
          className="py-1.5 px-3 align-middle min-w-[100px] text-center"
        >
          <div className="flex items-center justify-center">
            {renderTableCell(
              column.key,
              row,
              metricRenderAdaptersById,
              rowIndex,
            )}
          </div>
        </td>
      ))}
      {explanationColumns.map((column) => (
        <td
          key={column.key}
          className="py-1.5 px-3 align-middle text-sm text-coval-text-muted"
        >
          {explanationTable.getRowValues(row)[column.key] || "—"}
        </td>
      ))}
      <td className="py-1.5 px-3 align-middle text-sm text-coval-text-muted max-w-[200px]">
        <Tooltip
          content={
            <p className="text-sm whitespace-pre-wrap wrap-break-word max-w-md max-h-64 overflow-auto">
              {row.test_case_description?.trim() ||
                row.input_str?.trim() ||
                "—"}
            </p>
          }
          side="top"
          align="start"
        >
          <div
            className="overflow-hidden text-ellipsis w-full cursor-default"
            style={{
              maxHeight: "2.4rem",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              lineHeight: "1.2",
            }}
          >
            {row.test_case_description?.trim() || row.input_str?.trim() || "—"}
          </div>
        </Tooltip>
      </td>
    </tr>
  )
}
